type WindowItem = number | '…';

export function buildPageWindow(
  totalPages: number,
  currentPage: number,
  maxButtons: number,
  opts?: {
    alwaysShowFirst?: boolean; // keep "1" visible
    alwaysShowLast?: boolean; // force show last page
    revealLastOffset?: number; // when to start showing last page (pages from end)
  }
): WindowItem[] {
  const {
    alwaysShowFirst = true,
    alwaysShowLast = false,
    revealLastOffset = 4, // with 10 pages → show last at 6+
  } = opts ?? {};

  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Decide whether to show the last page number now
  const showLast =
    alwaysShowLast ||
    currentPage >= totalPages - revealLastOffset ||
    totalPages <= maxButtons;

  const showFirst = alwaysShowFirst;

  // How many numeric slots remain for the middle window (excluding first/last)
  let middleSlots = maxButtons;
  if (showFirst) middleSlots -= 1;
  if (showLast) middleSlots -= 1;
  middleSlots = Math.max(3, middleSlots); // keep a usable center window

  const half = Math.floor(middleSlots / 2);

  // Choose start/end around the current page
  let start = currentPage - half;
  let end = currentPage + half;

  // Boundaries (leave room for first/last if they are shown)
  const minStart = showFirst ? 2 : 1;
  const maxEnd = showLast ? totalPages - 1 : totalPages;

  if (start < minStart) {
    start = minStart;
    end = start + middleSlots - 1;
  }
  if (end > maxEnd) {
    end = maxEnd;
    start = end - middleSlots + 1;
  }

  const items: WindowItem[] = [];

  // First page
  if (showFirst) items.push(1);

  // Left gap/ellipsis
  if (start > (showFirst ? 3 : 2)) items.push('…');
  else if (showFirst) {
    for (let p = 2; p < start; p++) items.push(p);
  }

  // Middle window
  for (let p = start; p <= end; p++) items.push(p);

  // Right gap/ellipsis
  const rightBoundary = showLast ? totalPages - 1 : totalPages;
  if (end < rightBoundary - 1) items.push('…');
  else {
    for (let p = end + 1; p <= rightBoundary - (showLast ? 0 : 0); p++)
      items.push(p);
  }

  // Last page
  if (showLast) items.push(totalPages);

  return items;
}
